import React, { useState, useEffect } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import {
    LayoutDashboard,
    Users,
    FileText,
    BarChart,
    Bell,
    Calendar,
    ChevronLeft,
    Search,
    LogOut,
    Menu,
    DollarSign,
    History,
    Wallet,
    Tag,
    Shield,
    GraduationCap,
    ClipboardList,
    LibraryBig,
    X
} from 'lucide-react';
import { useAuth } from '../../services/AuthContext';
import './Admin.css';

export default function AdminLayout() {
    const { user, logout } = useAuth();
    const location = useLocation();
    const [sidebarOpen, setSidebarOpen] = useState(false);

    // Close sidebar on route change
    useEffect(() => {
        setSidebarOpen(false);
    }, [location.pathname]);

    // Close sidebar on ESC
    useEffect(() => {
        const handler = (e) => { if (e.key === 'Escape') setSidebarOpen(false); };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, []);

    const getPageTitle = () => {
        const path = location.pathname;
        if (path.includes('students')) return 'Student Directory';
        if (path.includes('exams')) return 'Assessment Architect';
        if (path.includes('results')) return 'Performance Analytics';
        if (path.includes('notifications')) return 'Broadcast Center';
        if (path.includes('free-sessions')) return 'Intake Pipeline';
        if (path.includes('finance/student')) return 'Student Financial Profile';
        if (path.includes('finance')) return 'Financial Systems';
        if (path.includes('instructors')) return 'Instructor Management';
        if (path.includes('groups')) return 'Groups & Job Codes';
        if (path.includes('instructor-payroll')) return 'Instructor Payroll';
        if (path.includes('audit-logs')) return 'System Audit Logs';
        if (path.includes('instructor-applications')) return 'Instructor Applications';
        if (path.includes('history')) return 'System Audit Trail';
        if (path.includes('session-report')) return 'Session Analytics Report';
        if (path.includes('academics')) return 'Academic Management';
        if (path.includes('attendance')) return 'Attendance Reports';
        return 'System Dashboard';
    };

    const navItems = [
        { to: '/admin', end: true, icon: LayoutDashboard, label: 'Dashboard' },
        { to: '/admin/students', icon: Users, label: 'Students' },
        { to: '/admin/exams', icon: FileText, label: 'Exams' },
        { to: '/admin/results', icon: BarChart, label: 'Results' },
        { to: '/admin/groups', icon: Tag, label: 'Groups' },
        { to: '/admin/academics', icon: LibraryBig, label: 'Academics' },
        { to: '/admin/attendance', icon: ClipboardList, label: 'Attendance' },
        { to: '/admin/notifications', icon: Bell, label: 'Broadcasting' },
        { to: '/admin/free-sessions', icon: Calendar, label: 'Pipeline' },
        { to: '/admin/finance', icon: DollarSign, label: 'Finance' },
        { to: '/admin/instructor-payroll', icon: Wallet, label: 'Payroll' },
        { to: '/admin/instructors', icon: GraduationCap, label: 'Instructors' },
        { to: '/admin/instructor-applications', icon: ClipboardList, label: 'Applications' },
        { to: '/admin/history', icon: History, label: 'Activity Log' },
        { to: '/admin/audit-logs', icon: Shield, label: 'Audit Logs' },
        { to: '/admin/session-report', icon: BarChart, label: 'Session Report' },
    ];

    const SidebarContent = () => (
        <>
            <div className="admin-sidebar-brand">
                <div style={{ fontWeight: 900, fontSize: '1.75rem', color: '#0f172a', letterSpacing: '-0.07em', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    CODE <span style={{ color: '#066af2' }}>WAVE</span>
                </div>
                <div style={{ fontSize: '0.7rem', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.15em', marginTop: '-0.25rem' }}>
                    SUPER ADMIN · PLATFORM OWNER
                </div>
            </div>

            <nav className="admin-sidebar-nav">
                {navItems.map(({ to, end, icon: Icon, label }) => (
                    <NavLink
                        key={to}
                        to={to}
                        end={end}
                        className={({ isActive }) => isActive ? 'sidebar-link-item active' : 'sidebar-link-item'}
                    >
                        <Icon size={20} />
                        {label}
                    </NavLink>
                ))}

                <div style={{ marginTop: 'auto', paddingTop: '2rem' }}>
                    <NavLink to="/" className="sidebar-link-item" style={{ opacity: 0.8, color: '#94a3b8' }}>
                        <ChevronLeft size={20} />
                        Exit to Site
                    </NavLink>
                </div>
            </nav>
        </>
    );

    return (
        <div className="admin-root-container">

            {/* Sidebar — Desktop: always visible | Mobile: drawer */}
            <aside className="admin-layout-sidebar admin-sidebar--desktop">
                <SidebarContent />
            </aside>

            {/* Mobile Drawer Overlay */}
            {sidebarOpen && (
                <div
                    className="admin-sidebar-overlay"
                    onClick={() => setSidebarOpen(false)}
                    aria-label="Close menu"
                />
            )}

            {/* Mobile Drawer */}
            <aside className={`admin-layout-sidebar admin-sidebar--mobile ${sidebarOpen ? 'open' : ''}`}>
                <button
                    className="admin-sidebar-close-btn"
                    onClick={() => setSidebarOpen(false)}
                    aria-label="Close sidebar"
                >
                    <X size={20} />
                </button>
                <SidebarContent />
            </aside>

            {/* Main Workspace Area */}
            <div className="admin-layout-main">

                {/* Unified Topbar */}
                <header className="admin-layout-topbar">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        {/* Hamburger — visible on tablet/mobile */}
                        <button
                            className="admin-hamburger-btn"
                            onClick={() => setSidebarOpen(true)}
                            aria-label="Open menu"
                        >
                            <Menu size={22} />
                        </button>

                        <div style={{ fontSize: '1.15rem', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.02em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '200px' }}>
                            {getPageTitle()}
                        </div>

                        <div style={{ height: '24px', width: '1px', background: '#e2e8f0', flexShrink: 0 }} className="admin-topbar-divider" />

                        <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }} className="admin-topbar-search">
                            <Search size={16} color="#94a3b8" style={{ position: 'absolute', left: '1rem' }} />
                            <input
                                type="text"
                                placeholder="Search platform data..."
                                style={{ padding: '0.625rem 1rem 0.625rem 2.75rem', border: '1px solid #e2e8f0', borderRadius: '0.75rem', fontSize: '0.875rem', width: '260px', outline: 'none', background: '#f8fafc' }}
                            />
                        </div>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexShrink: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }} className="admin-topbar-user">
                            <div style={{ textAlign: 'right' }} className="admin-topbar-user-text">
                                <div style={{ fontSize: '0.875rem', fontWeight: 800, color: '#0f172a' }}>{user?.fullName || 'Administrator'}</div>
                                <div style={{ fontSize: '0.65rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>Current Session</div>
                            </div>
                            <div style={{ width: '40px', height: '40px', borderRadius: '12px', background: '#0f172a', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, flexShrink: 0 }}>
                                {user?.fullName ? user.fullName[0] : 'A'}
                            </div>
                        </div>
                        <button
                            onClick={logout}
                            style={{ padding: '0.5rem', borderRadius: '0.75rem', border: '1px solid #fee2e2', background: '#fef2f2', color: '#dc2626', cursor: 'pointer', flexShrink: 0 }}
                            title="Sign Out"
                        >
                            <LogOut size={18} />
                        </button>
                    </div>
                </header>

                {/* Content Viewport */}
                <main className="admin-layout-content">
                    <div className="admin-container">
                        <Outlet />
                    </div>
                </main>

            </div>
        </div>
    );
}
